import OnvoBase from "../base.js";
import { Question } from "../types/index.js";

// Question endpoints
export class OnvoQuestions extends OnvoBase {
  /**
   * Lists all questions for a given dashboard.
   *
   * @param {Object} filters - The filters to apply to the question list.
   * @param {string} filters.dashboard - The ID of the dashboard to list questions for.
   * @returns {Promise<Question[]>} A promise that resolves to an array of questions.
   */
  list(filters: { dashboard: string }): Promise<Question[]> {
    return this.fetchBase(
      "/api/questions?dashboard=" + filters.dashboard
    ) as Promise<Question[]>;
  }

  /**
   * Creates a new question.
   *
   * @param {Object} payload - The payload for the question creation.
   * @param {string} payload.query - The query string for the question.
   * @param {string} payload.dashboard - The ID of the dashboard to create the question in.
   * @returns {Promise<Question & { _meta: {status: string, url: string} }>} A promise that resolves to the created question.
   */
  create(payload: {
    query: string;
    dashboard: string;
  }): Promise<Question & { _meta: { status: string, url: string } }> {
    return this.fetchBase("/api/questions", "POST", {
      dashboard: payload.dashboard,
      query: payload.query || "",
    }) as Promise<Question & { _meta: { status: string, url: string } }>;
  }

  /**
   * Deletes a question.
   *
   * @param {string} id - The ID of the question to delete.
   * @returns {Promise<{ success: boolean }>} A promise that resolves to an object indicating success.
   */
  delete(id: string): Promise<{ success: boolean }> {
    return this.fetchBase("/api/questions/" + id, "DELETE") as Promise<{
      success: boolean;
    }>;
  }

  /**
   * Updates a question.
   *
   * @param {string} id - The ID of the question to update.
   * @param {Partial<Question>} body - The updated question data.
   * @returns {Promise<Question>} A promise that resolves to the updated question.
   */
  update(id: string, body: Partial<Question>): Promise<Question> {
    return this.fetchBase(
      "/api/questions/" + id,
      "PUT",
      body
    ) as Promise<Question>;
  }
}
